import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { RecentRegistration } from '../../../types/types';
import { Title } from './Tournaments';
import { convertUtcToLocal } from '../../../utils/dateUtils';
import {
  TEXT_SIZES,
  COLORS,
  DateFormatStrings,
  FONT_WEIGHTS,
} from '../../../utils/constants';
import { getEventRecentRegistrations } from '../../../api/requests/events';

type Props = { eventId: number };

const RecentRegistrations: React.FC<Props> = ({ eventId }) => {
  const [data, setData] = useState<RecentRegistration[]>([]);

  const fetchRecentRegistrations = useCallback(async () => {
    if (!eventId) return;
    try {
      const res = await getEventRecentRegistrations(eventId);
      setData(res?.value?.rows ?? []);
    } catch (err) {
      console.error('Error fetching recent registrations:', err);
    }
  }, [eventId]);

  useEffect(() => {
    fetchRecentRegistrations();
  }, [fetchRecentRegistrations]);

  return (
    <Container>
      <Title>Recent Registrations ({data?.length ?? 0})</Title>
      <ScrollableContainer>
        {(!data || data.length === 0) && <Empty>No registrations yet.</Empty>}
        {data?.map((reg: any) => (
          <Card
            key={reg.tournamentParticipantId || reg.participantId || reg.id}
          >
            <LeftPart>
              <Name>{reg.participantName}</Name>
              <SubText>{reg.participantEmail ?? ''}</SubText>
              <SubText>{reg.participantPhone ?? ''}</SubText>
            </LeftPart>
            <SubText>
              {reg.registrationDate
                ? convertUtcToLocal(
                    reg.registrationDate as string,
                    DateFormatStrings.DD_MMM_YYYY,
                  )
                : '--'}
            </SubText>
          </Card>
        ))}
      </ScrollableContainer>
    </Container>
  );
};

export default RecentRegistrations;

/* Styled */
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
  overflow: hidden;
`;

const Empty = styled.div`
  color: ${COLORS.grey.secondary};
  font-style: italic;
`;

const Card = styled.div`
  background: ${COLORS.white.primary};
  padding: 12px;
  min-height: 98px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
  border: 0.5px solid ${COLORS.grey.lightMedium};
  border-radius: 16px;
`;

const LeftPart = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Name = styled.div`
  font-weight: ${FONT_WEIGHTS.BOLD};
  font-size: ${TEXT_SIZES.SM}px;
`;

const SubText = styled.div`
  color: ${COLORS.grey.medium};
  font-size: ${TEXT_SIZES.SM}px;
  font-weight: ${FONT_WEIGHTS.REGULAR};
`;

const ScrollableContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
`;
